import * as THREE from "three";
import { WORLD } from "../config";
import { makeBurgeeTexture, type ClubMaterials } from "../materials";

export function buildClubhouse(mats: ClubMaterials): THREE.Group {
  const g = new THREE.Group();
  g.name = "clubhouse";
  g.position.set(0, 0, WORLD.basinSouth - 14);

  const plinth = new THREE.Mesh(new THREE.BoxGeometry(36, 1.1, 22), mats.whiteConcrete);
  plinth.position.set(0, 1.55, -1);
  plinth.castShadow = true;
  plinth.receiveShadow = true;
  g.add(plinth);

  const floorY = 2.15;
  const slab = new THREE.Mesh(new THREE.BoxGeometry(34, 0.28, 18), mats.whiteConcrete);
  slab.position.set(0, floorY, 1.2);
  slab.castShadow = true;
  g.add(slab);

  // Cantilevered north terrace over the quay
  const terrace = new THREE.Mesh(new THREE.BoxGeometry(28, 0.22, 10), mats.whiteConcrete);
  terrace.position.set(0, floorY + 0.02, 10.4);
  terrace.castShadow = true;
  terrace.receiveShadow = true;
  g.add(terrace);

  const cols: [number, number][] = [
    [-14, 4],
    [14, 4],
    [-14, -6],
    [14, -6],
    [-8, 12],
    [8, 12],
  ];
  for (const [x, z] of cols) {
    const col = new THREE.Mesh(new THREE.BoxGeometry(0.7, 7.4, 0.7), mats.whiteConcrete);
    col.position.set(x, floorY + 3.7, z);
    col.castShadow = true;
    g.add(col);
  }

  const glassWall = (w: number, h: number, d: number, x: number, y: number, z: number, rotY = 0) => {
    const pane = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mats.glass);
    pane.position.set(x, y, z);
    pane.rotation.y = rotY;
    g.add(pane);
  };

  // Harbour facade is glass; other sides are white concrete with punched windows.
  glassWall(26, 6.4, 0.12, 0, floorY + 3.4, 9.8);
  const southWall = new THREE.Mesh(new THREE.BoxGeometry(27, 6.6, 0.45), mats.whiteConcrete);
  southWall.position.set(0, floorY + 3.4, -6.7);
  southWall.castShadow = true;
  g.add(southWall);
  for (const x of [-8, 0, 8]) {
    glassWall(4.2, 2.4, 0.1, x, floorY + 3.6, -6.92);
  }
  const westWall = new THREE.Mesh(new THREE.BoxGeometry(0.45, 6.6, 16.4), mats.whiteConcrete);
  westWall.position.set(-13.5, floorY + 3.4, 1.6);
  westWall.castShadow = true;
  g.add(westWall);
  const eastWall = westWall.clone();
  eastWall.position.x = 13.5;
  g.add(eastWall);
  glassWall(0.08, 4.2, 8, -13.74, floorY + 3.5, 2.4);
  glassWall(0.08, 4.2, 8, 13.74, floorY + 3.5, 2.4);

  // Mullions
  for (const x of [-8.8, 0, 8.8]) {
    const m = new THREE.Mesh(new THREE.BoxGeometry(0.12, 6.4, 0.14), mats.navy);
    m.position.set(x, floorY + 3.4, 9.86);
    g.add(m);
  }

  const roof = new THREE.Mesh(new THREE.BoxGeometry(38, 0.35, 24), mats.whiteConcrete);
  roof.position.set(0, floorY + 6.85, 1);
  roof.castShadow = true;
  g.add(roof);

  const louvers = new THREE.Group();
  for (let i = 0; i < 14; i++) {
    const l = new THREE.Mesh(new THREE.BoxGeometry(30, 0.08, 0.35), mats.navy);
    l.position.set(0, 0, i * 0.7);
    l.rotation.x = 0.4;
    louvers.add(l);
  }
  louvers.position.set(0, floorY + 7.15, 4);
  g.add(louvers);

  const fascia = new THREE.Mesh(new THREE.BoxGeometry(18, 1.9, 0.12), mats.white);
  fascia.position.set(0, floorY + 7.55, 13.05);
  g.add(fascia);
  const logoMat = new THREE.MeshBasicMaterial({
    transparent: true,
    depthWrite: false,
    color: 0xffffff,
  });
  const logoW = 16.4;
  const logoH = logoW * (56 / 591);
  const logo = new THREE.Mesh(new THREE.PlaneGeometry(logoW, logoH), logoMat);
  logo.position.set(0, floorY + 7.55, 13.14);
  g.add(logo);
  const loader = new THREE.TextureLoader();
  loader.load(`${import.meta.env.BASE_URL}brand/ranmarine-logo.png`, (tex) => {
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 8;
    tex.premultiplyAlpha = false;
    logoMat.map = tex;
    logoMat.needsUpdate = true;
  });

  const pool = new THREE.Mesh(new THREE.BoxGeometry(16, 0.45, 5.2), mats.pool);
  pool.position.set(0, floorY + 0.18, 11.2);
  g.add(pool);
  const poolEdge = new THREE.Mesh(new THREE.BoxGeometry(17.2, 0.16, 6.2), mats.whiteConcrete);
  poolEdge.position.set(0, floorY + 0.12, 11.2);
  g.add(poolEdge);

  interior(g, mats, floorY);
  furniture(g, mats, floorY);
  flagpole(g, mats);

  const stair = new THREE.Mesh(new THREE.BoxGeometry(4.2, 1.1, 6.5), mats.whiteConcrete);
  stair.position.set(10, 1.55, 14.5);
  stair.castShadow = true;
  g.add(stair);
  for (let i = 0; i < 6; i++) {
    const step = new THREE.Mesh(new THREE.BoxGeometry(4.2, 0.18, 0.55), mats.whiteConcrete);
    step.position.set(10, 1.1 + i * 0.18, 17.4 - i * 0.45);
    g.add(step);
  }

  return g;
}

function interior(g: THREE.Group, mats: ClubMaterials, floorY: number): void {
  const floor = new THREE.Mesh(new THREE.BoxGeometry(25.5, 0.08, 15.2), mats.interior);
  floor.position.set(0, floorY + 0.18, 1.5);
  g.add(floor);

  const desk = new THREE.Mesh(new THREE.BoxGeometry(5.5, 1.05, 1.4), mats.navy);
  desk.position.set(0, floorY + 0.75, 6.2);
  g.add(desk);

  const bar = new THREE.Mesh(new THREE.BoxGeometry(8.5, 1.1, 1.6), mats.teak);
  bar.position.set(-6.5, floorY + 0.78, -2.4);
  g.add(bar);

  for (const x of [-4, 0, 4]) {
    const lamp = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.4, 0.18, 12), mats.white);
    lamp.position.set(x, floorY + 5.8, 2);
    g.add(lamp);
    const light = new THREE.PointLight(0xffd8a8, 8, 18, 2);
    light.position.set(x, floorY + 5.5, 2);
    g.add(light);
  }
}

function furniture(g: THREE.Group, mats: ClubMaterials, floorY: number): void {
  for (const x of [-9, -5, 5, 9]) {
    const lounge = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.28, 0.7), mats.cushion);
    lounge.position.set(x, floorY + 0.42, 13.6);
    lounge.castShadow = true;
    g.add(lounge);
    const back = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.55, 0.12), mats.cushion);
    back.position.set(x, floorY + 0.72, 13.28);
    g.add(back);
  }

  for (const x of [-10, 10]) {
    const planter = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.7, 2.4), mats.whiteConcrete);
    planter.position.set(x, floorY + 0.5, 8.2);
    g.add(planter);
  }
}

function flagpole(g: THREE.Group, mats: ClubMaterials): void {
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.07, 9.5, 8), mats.chrome);
  pole.position.set(16.5, 6.8, 12);
  pole.castShadow = true;
  g.add(pole);

  const burgee = new THREE.Mesh(
    new THREE.PlaneGeometry(2.1, 1.25),
    new THREE.MeshStandardMaterial({
      map: makeBurgeeTexture(),
      transparent: true,
      side: THREE.DoubleSide,
      roughness: 0.7,
    }),
  );
  burgee.position.set(17.55, 10.7, 12);
  burgee.name = "burgee";
  g.add(burgee);
}
